import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { setAlpha } from '../../../design-tokens/utils';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../locale';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { ButtonVariant } from '../../Button';
import { BaseSplitButton } from '../../SplitButton/__internal__/BaseSplitButton';
import selectClassMap from '../../Select/Select.css.json';
import '../../Select/Select.css';
import cn from './MultiBreadcrumbDesign.css.json';
import './MultiBreadcrumbDesign.css';
import { jsx as _jsx } from "react/jsx-runtime";
export const MultiBreadcrumbDesign = /*#__PURE__*/forwardRef(function MultiBreadcrumbDesign(props, ref) {
  const labels = useMemo(() => props.values.map(text => ({
    text
  })), [props.values]);
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const tokenOverrides = props.tokens?.();
  const setTruncationRef = useTruncationTooltip();
  const tokens = {
    ...getMultiBreadcrumbDesignTokens(dt, {
      tokens: props.tokens,
      variant: props.variant
    }),
    ...props.theme?.(dt)
  };
  const buttonVariant = dt.themeVariant === 'light' && props.variant === 'primary-light' ? ButtonVariant.PrimaryIcon : ButtonVariant.Icon;
  const renderRef = useRef(true);
  useEffect(() => {
    renderRef.current = false;
  }, []);
  const [exitAnimClass, setExitAnimClass] = useState('');
  useEffect(() => {
    if (props.animated && props.isLastItem && props.hideAnimation) {
      setExitAnimClass(cn.exitAnimation);
    } else {
      setExitAnimClass('');
    }
  }, [props.animated, props.isLastItem, props.hideAnimation]);
  const duration = !props.hideAnimation ? 0 : 0.3;
  const position = isRTL ? '100%' : '-100%';
  const breadcrumbAnimation = {
    closed: {
      translate: position
    },
    open: {
      translate: 0,
      transition: {
        duration,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  return /*#__PURE__*/_jsx(AnimatePresence, {
    mode: "wait",
    children: /*#__PURE__*/_jsx(motion.div, {
      variants: breadcrumbAnimation,
      animate: "open",
      initial: "closed",
      children: /*#__PURE__*/_jsx(BaseSplitButton, {
        items: labels,
        text: labels[props.selectedIndex]?.text,
        onPress: () => props.onPress?.(props.index),
        onOpen: () => {
          props.onOpen?.(props.index);
        },
        onClose: () => {
          props.onClose?.(props.index);
        },
        onItemSelected: selectedIndex => props.onPressItem?.(props.index, selectedIndex ?? 0),
        focusable: props.focused,
        variant: buttonVariant,
        style: {
          ...tokens,
          ...props.style
        },
        ref: element => {
          setTruncationRef(element);
          setRef(ref, element);
        },
        className: cx(selectClassMap.select, cn.dropdown, selectClassMap.label, cn.dropdownLabel, cn.itemText, cn.interactive, exitAnimClass),
        ariaCurrent: props.isCurrent ? props.ariaCurrent ?? 'page' : undefined,
        testId: props.testId,
        ariaRoleDescription: '',
        theme: () => {
          const tokens = {
            '--button-filter': 'none',
            '--button-hover-filter': 'none',
            '--button-active-filter': 'none',
            '--split-button-divider': 'transparent',
            '--button-active-bd': 'transparent'
          };
          const overrides = tokenOverrides && Object.keys(tokenOverrides).length !== 0 ? {
            '--button-fg': tokenOverrides.fg,
            '--button-fontFamily': tokenOverrides.fontFamily,
            '--button-hover-fg': tokenOverrides.fg,
            '--button-active-fg': tokenOverrides.fg
          } : {};
          if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
            return {
              ...tokens,
              '--button-hover-bg': setAlpha(dt.tokens['color-text-primary-inverse'], 0.15),
              '--button-active-bg': setAlpha(dt.tokens['color-text-primary-inverse'], 0.25),
              ...overrides
            };
          }
          return {
            ...tokens,
            ...overrides
          };
        },
        buttonClassName: cx(cn.actionButton, props.isCurrent ? cn.current : undefined),
        onFocus: props.onFocus
      })
    }, "multi-breadcrumb-item")
  });
});
export const getMultiBreadcrumbDesignTokens = (dt, opts) => {
  let variantStyle = {
    fg: dt.tokens['color-action-text-control-normal'],
    bg: dt.tokens['color-action-background-control-normal'],
    bd: dt.tokens['color-action-border-control-read-only'],
    focus: 'var(--focus-light-bg)'
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    variantStyle = {
      fg: dt.tokens['color-text-primary-inverse'],
      bg: setAlpha(dt.tokens['color-text-primary-inverse'], 0.1),
      bd: dt.tokens['color-text-primary-inverse'],
      focus: 'var(--focus-dark-bg)'
    };
  }
  const tokenOverrides = opts?.tokens?.();
  return {
    ...renderDesignTokens({
      filter: 'none',
      'font-weight-thick': dt.tokens['font-style-weight-700'],
      'font-family': tokenOverrides?.fontFamily ?? 'inherit'
    }, 'breadcrumb-text'),
    ...renderDesignTokens({
      ...variantStyle,
      fg: tokenOverrides?.fg ?? 'inherit',
      'focus-offset': dt.themeVariant === 'high-contrast' ? 'calc(-1 * var(--focus-width))' : 'calc(-1 * var(--focus-width) - 2px)'
    }, 'breadcrumb-dropdown'),
    ...renderDesignTokens({
      'squish-xs': dt.tokens['space-squish-xs'],
      'mg-xs': dt.tokens['space-inline-xs'],
      'anim-dur-enter': dt.tokens['duration-300'],
      'anim-tf-enter': dt.tokens['ease-in']
    }, 'breadcrumb'),
    ...(!dt.enableNovaTokens && getMultiBreadcrumbLegacyDesignTokens(dt, opts))
  };
};
const getMultiBreadcrumbLegacyDesignTokens = (dt, opts) => {
  let variantStyle = {
    ...dt.reportTokens.stateStandard.normal,
    focus: 'var(--focus-light-bg)'
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    variantStyle = {
      fg: dt.c.white,
      bg: setAlpha(dt.c.white, 0.1),
      bd: dt.c.white,
      focus: 'var(--focus-dark-bg)'
    };
  }
  return {
    ...renderDesignTokens({
      filter: dt.reportTokens.stateStandard.normal.filter,
      'font-weight-thick': dt.reportTokens.font.weight.thick
    }, 'breadcrumb-text'),
    ...renderDesignTokens({
      ...variantStyle,
      'focus-offset': dt.themeVariant === 'high-contrast' ? 'calc(-1 * var(--focus-width))' : 'calc(-1 * var(--focus-width) - 2px)'
    }, 'breadcrumb-dropdown')
  };
};
//# sourceMappingURL=MultiBreadcrumbDesign.js.map